import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { getApiBaseUrl } from '../utils/axios';
import './Settings.css';
import './AdminSettings.css';

const CmsAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('blog'); // 'blog' | 'pages'
  const [blogPosts, setBlogPosts] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);

  // Edit form state: which item is being edited (id or 'new')
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [editingPageId, setEditingPageId] = useState(null);
  const [blogForm, setBlogForm] = useState({
    topic: '', brief: '', slug: '', title: '', meta_title: '', meta_description: '', body: '', cover_image_url: '', published: true, published_at: ''
  });
  const [pageForm, setPageForm] = useState({
    slug: '', title: '', meta_title: '', meta_description: '', body: '', published: true, sort_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [blogAiLoading, setBlogAiLoading] = useState(null);
  const blogImageInputRef = useRef(null);
  const blogBodyRef = useRef('');

  useEffect(() => {
    loadBlog();
    loadPages();
  }, []);

  const loadBlog = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/cms/admin/blog');
      setBlogPosts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setBlogPosts([]);
      if (e.response?.status !== 404) setError('Blog laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const loadPages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/cms/admin/pages');
      setPages(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setPages([]);
      if (e.response?.status !== 404) setError('Pagina\'s laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  const startEditBlog = (post) => {
    if (!post) {
      setEditingBlogId('new');
      blogBodyRef.current = '';
      setBlogForm({
        topic: '', brief: '', slug: '', title: '', meta_title: '', meta_description: '', body: '', cover_image_url: '', published: true,
        published_at: new Date().toISOString().split('T')[0]
      });
    } else {
      setEditingBlogId(post.id);
      blogBodyRef.current = post.body || '';
      setBlogForm({
        topic: '', brief: '',
        slug: post.slug || '',
        title: post.title || '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        body: post.body || '',
        cover_image_url: post.cover_image_url || '',
        published: post.published !== false,
        published_at: (post.published_at || '').split('T')[0] || new Date().toISOString().split('T')[0]
      });
    }
  };

  const startEditPage = (page) => {
    if (!page) {
      setEditingPageId('new');
      setPageForm({
        slug: '', title: '', meta_title: '', meta_description: '', body: '', published: true, sort_order: 0
      });
    } else {
      setEditingPageId(page.id);
      setPageForm({
        slug: page.slug || '',
        title: page.title || '',
        meta_title: page.meta_title || '',
        meta_description: page.meta_description || '',
        body: page.body || '',
        published: page.published !== false,
        sort_order: page.sort_order != null ? page.sort_order : 0
      });
    }
  };

  const saveBlog = async (e) => {
    e.preventDefault();
    const form = e.target;
    const bodyEl = form.querySelector('textarea[name="blog-body"]');
    const bodyValue = (bodyEl && bodyEl.value !== undefined) ? bodyEl.value : (blogBodyRef.current || blogForm.body || '');
    setSaving(true);
    setError(null);
    try {
      const payload = {
        slug: blogForm.slug.trim(),
        title: blogForm.title.trim(),
        meta_title: blogForm.meta_title.trim() || null,
        meta_description: blogForm.meta_description.trim() || null,
        cover_image_url: blogForm.cover_image_url.trim() || null,
        body: bodyValue,
        published: blogForm.published,
        published_at: blogForm.published_at ? blogForm.published_at + 'T12:00:00.000Z' : new Date().toISOString()
      };
      if (editingBlogId === 'new') {
        await api.post('/api/cms/admin/blog', payload);
        showSuccess('Blogpost aangemaakt.');
      } else {
        await api.put(`/api/cms/admin/blog/${editingBlogId}`, payload);
        showSuccess('Blogpost bijgewerkt.');
      }
      setEditingBlogId(null);
      loadBlog();
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const savePage = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        slug: pageForm.slug.trim(),
        title: pageForm.title.trim(),
        meta_title: pageForm.meta_title.trim() || null,
        meta_description: pageForm.meta_description.trim() || null,
        body: pageForm.body || '',
        published: pageForm.published,
        sort_order: parseInt(pageForm.sort_order, 10) || 0
      };
      if (editingPageId === 'new') {
        await api.post('/api/cms/admin/pages', payload);
        showSuccess('Pagina aangemaakt.');
      } else {
        await api.put(`/api/cms/admin/pages/${editingPageId}`, payload);
        showSuccess('Pagina bijgewerkt.');
      }
      setEditingPageId(null);
      loadPages();
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm('Deze blogpost definitief verwijderen?')) return;
    try {
      await api.delete(`/api/cms/admin/blog/${id}`);
      showSuccess('Blogpost verwijderd.');
      setEditingBlogId(null);
      loadBlog();
    } catch (err) {
      setError(err.response?.data?.error || 'Verwijderen mislukt');
    }
  };

  const handleSeedBlog = async () => {
    setSeedLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/cms/admin/seed-blog');
      showSuccess(res.data?.message || 'Import voltooid.');
      loadBlog();
    } catch (err) {
      setError(err.response?.data?.error || 'Import mislukt');
    } finally {
      setSeedLoading(false);
    }
  };

  const deletePage = async (id) => {
    if (!window.confirm('Deze pagina definitief verwijderen?')) return;
    try {
      await api.delete(`/api/cms/admin/pages/${id}`);
      showSuccess('Pagina verwijderd.');
      setEditingPageId(null);
      loadPages();
    } catch (err) {
      setError(err.response?.data?.error || 'Verwijderen mislukt');
    }
  };

  const handleBlogGenerateText = async () => {
    if (!blogForm.topic?.trim()) {
      setError('Vul een onderwerp in om een blogpost te genereren.');
      return;
    }
    setBlogAiLoading('generate');
    setError(null);
    try {
      const res = await api.post('/api/cms/admin/blog/generate-text', {
        topic: blogForm.topic.trim(),
        description: blogForm.brief?.trim() || undefined
      });
      const d = res.data;
      const updates = {};
      if (d.body) { blogBodyRef.current = d.body; updates.body = d.body; }
      if (d.title) updates.title = d.title;
      if (d.slug) updates.slug = d.slug;
      if (d.meta_title) updates.meta_title = d.meta_title;
      if (d.meta_description) updates.meta_description = d.meta_description;
      setBlogForm(f => ({ ...f, ...updates }));
      showSuccess('Blogpost gegenereerd inclusief slug, meta titel en meta description.');
    } catch (err) {
      setError(err.response?.data?.error || 'Genereren mislukt');
    } finally {
      setBlogAiLoading(null);
    }
  };

  const handleBlogRewriteText = async () => {
    if (!blogForm.body?.trim()) {
      setError('Vul eerst inhoud in om te herschrijven.');
      return;
    }
    setBlogAiLoading('rewrite');
    setError(null);
    try {
      const res = await api.post('/api/cms/admin/blog/rewrite-text', { body: blogForm.body });
      if (res.data.body) {
        blogBodyRef.current = res.data.body;
        setBlogForm(f => ({ ...f, body: res.data.body }));
      }
      showSuccess('Tekst herschreven.');
    } catch (err) {
      setError(err.response?.data?.error || 'Herschrijven mislukt');
    } finally {
      setBlogAiLoading(null);
    }
  };

  const handleBlogUploadImage = (e) => {
    const file = e.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      api.post('/api/cms/admin/blog/upload-image', { image: dataUrl })
        .then(res => {
          const url = res.data?.url;
          if (url) setBlogForm(f => ({ ...f, cover_image_url: url }));
          showSuccess('Afbeelding geüpload.');
        })
        .catch(err => setError(err.response?.data?.error || 'Upload mislukt'));
    };
    reader.readAsDataURL(file);
    if (blogImageInputRef.current) blogImageInputRef.current.value = '';
  };

  return (
    <div className="settings-page admin-settings-page">
      <div className="settings-page-header">
        <h2>Content (CMS)</h2>
        <button type="button" className="btn btn-secondary btn-small" onClick={() => navigate('/instellingen/admin')}>
          ← Terug naar Admin
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button
          type="button"
          className={`btn ${activeTab === 'blog' ? 'btn-primary' : 'btn-secondary'} btn-small`}
          onClick={() => setActiveTab('blog')}
        >
          Blog
        </button>
        <button
          type="button"
          className={`btn ${activeTab === 'pages' ? 'btn-primary' : 'btn-secondary'} btn-small`}
          onClick={() => setActiveTab('pages')}
        >
          Pagina&apos;s
        </button>
      </div>

      {activeTab === 'blog' && (
        <div className="settings-section">
          <h3>Blogposts</h3>
          <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: 12 }}>
            Beheer artikelen voor <strong>/blog</strong>. SEO: vul meta titel en meta description in voor zoekmachines.
          </p>
          <div style={{ marginBottom: 12 }}>
            <button type="button" className="btn btn-primary btn-small" onClick={() => startEditBlog(null)}>
              + Nieuwe blogpost
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-small"
              style={{ marginLeft: 8 }}
              onClick={handleSeedBlog}
              disabled={seedLoading}
            >
              {seedLoading ? 'Bezig...' : 'Importeer uit blog-posts.json'}
            </button>
          </div>
          {loading && blogPosts.length === 0 ? (
            <p style={{ color: '#6b7280' }}>Laden...</p>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table className="admin-users-table cms-blog-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Slug</th>
                    <th>Titel</th>
                    <th>Gepubliceerd</th>
                    <th>Datum</th>
                    <th className="cms-blog-actions-th">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {blogPosts.map((p) => (
                    <tr key={p.id}>
                      <td><code style={{ fontSize: 12 }}>{p.slug}</code></td>
                      <td>{p.title}</td>
                      <td>{p.published ? 'Ja' : 'Nee'}</td>
                      <td>{(p.published_at || '').split('T')[0]}</td>
                      <td className="cms-blog-actions-td">
                        <a
                          href={`${window.location.origin}/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-small"
                        >
                          Bekijk artikel
                        </a>
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => startEditBlog(p)}>Bewerken</button>
                        <button type="button" className="btn btn-secondary btn-small cms-btn-delete" onClick={() => deleteBlog(p.id)}>Verwijderen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {blogPosts.length === 0 && !loading && (
                <p style={{ padding: 16, color: '#6b7280', margin: 0 }}>Nog geen blogposts. Klik op &quot;Nieuwe blogpost&quot; of &quot;Importeer uit blog-posts.json&quot;.</p>
              )}
            </div>
          )}

          {(editingBlogId === 'new' || editingBlogId) && (
            <form onSubmit={saveBlog} style={{ marginTop: 24, padding: 20, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <h4 style={{ marginTop: 0 }}>{editingBlogId === 'new' ? 'Nieuwe blogpost' : 'Blogpost bewerken'}</h4>

              <div style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 20 }}>
                <div className="input-group">
                  <label>Onderwerp</label>
                  <input
                    type="text"
                    value={blogForm.topic}
                    onChange={e => setBlogForm(f => ({ ...f, topic: e.target.value }))}
                    placeholder="Bijv. Hoe maak je een professionele offerte"
                  />
                </div>
                <div className="input-group">
                  <label>Korte omschrijving</label>
                  <textarea
                    value={blogForm.brief}
                    onChange={e => setBlogForm(f => ({ ...f, brief: e.target.value }))}
                    rows={2}
                    placeholder="Waar moet het artikel over gaan? Geef eventueel specifieke punten of invalshoeken."
                    style={{ fontFamily: 'inherit', fontSize: 13, width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-small"
                    onClick={handleBlogGenerateText}
                    disabled={blogAiLoading !== null || !blogForm.topic?.trim()}
                  >
                    {blogAiLoading === 'generate' ? 'Bezig met schrijven...' : '1. Schrijf tekst voor een goede blogpost'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={handleBlogRewriteText}
                    disabled={blogAiLoading !== null || !blogForm.body?.trim()}
                  >
                    {blogAiLoading === 'rewrite' ? 'Bezig...' : '2. Herschrijf tekst'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={() => blogImageInputRef.current?.click()}
                  >
                    3. Upload blogafbeelding
                  </button>
                  <input
                    ref={blogImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleBlogUploadImage}
                  />
                </div>
                <p style={{ fontSize: 11, color: '#6b7280', margin: '8px 0 0' }}>
                  De AI genereert automatisch: titel, slug, meta titel, meta description en de volledige blogpost.
                </p>
              </div>

              <div className="input-group">
                <label>Slug (URL-deel)</label>
                <input
                  type="text"
                  value={blogForm.slug}
                  onChange={e => setBlogForm(f => ({ ...f, slug: e.target.value }))}
                  placeholder="wordt-automatisch-ingevuld"
                  required
                />
              </div>
              <div className="input-group">
                <label>Titel</label>
                <input
                  type="text"
                  value={blogForm.title}
                  onChange={e => setBlogForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div className="input-group">
                <label>Meta titel (SEO)</label>
                <input
                  type="text"
                  value={blogForm.meta_title}
                  onChange={e => setBlogForm(f => ({ ...f, meta_title: e.target.value }))}
                  placeholder="Wordt automatisch ingevuld"
                />
              </div>
              <div className="input-group">
                <label>Meta description (SEO, max ~160 tekens)</label>
                <input
                  type="text"
                  value={blogForm.meta_description}
                  onChange={e => setBlogForm(f => ({ ...f, meta_description: e.target.value }))}
                  placeholder="Wordt automatisch ingevuld"
                />
              </div>
              <div className="input-group">
                <label>Blogafbeelding (cover)</label>
                {blogForm.cover_image_url && (
                  <div style={{ marginBottom: 8 }}>
                    <img
                      src={blogForm.cover_image_url.startsWith('http') ? blogForm.cover_image_url : `${getApiBaseUrl()}${blogForm.cover_image_url}`}
                      alt="Cover"
                      style={{ maxWidth: 280, maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                  </div>
                )}
                <input
                  type="text"
                  value={blogForm.cover_image_url}
                  onChange={e => setBlogForm(f => ({ ...f, cover_image_url: e.target.value }))}
                  placeholder="/blog-images/... of URL"
                />
              </div>
              <div className="input-group">
                <label>Inhoud (HTML)</label>
                <textarea
                  name="blog-body"
                  value={blogForm.body}
                  onChange={e => {
                    const v = e.target.value;
                    blogBodyRef.current = v;
                    setBlogForm(f => ({ ...f, body: v }));
                  }}
                  rows={12}
                  style={{ fontFamily: 'inherit', fontSize: 13, width: '100%' }}
                />
              </div>
              <div className="input-group">
                <label>Publicatiedatum</label>
                <input
                  type="date"
                  value={blogForm.published_at}
                  onChange={e => setBlogForm(f => ({ ...f, published_at: e.target.value }))}
                />
              </div>
              <div className="input-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={blogForm.published}
                    onChange={e => setBlogForm(f => ({ ...f, published: e.target.checked }))}
                  />
                  <span>Gepubliceerd (zichtbaar op site)</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Opslaan...' : 'Opslaan'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingBlogId(null)}>Annuleren</button>
                <a href="/blog" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: 14, color: '#1B3A6B' }}>Terug naar Blogpagina →</a>
              </div>
            </form>
          )}
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="settings-section">
          <h3>Pagina&apos;s</h3>
          <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: 12 }}>
            Extra pagina&apos;s op <strong>/pagina/:slug</strong> (bijv. /pagina/over-ons). SEO per pagina.
          </p>
          <button type="button" className="btn btn-primary btn-small" onClick={() => startEditPage(null)} style={{ marginBottom: 12 }}>
            + Nieuwe pagina
          </button>
          {loading && pages.length === 0 ? (
            <p style={{ color: '#6b7280' }}>Laden...</p>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table className="admin-users-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Slug</th>
                    <th>Titel</th>
                    <th>Volgorde</th>
                    <th>Gepubliceerd</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p) => (
                    <tr key={p.id}>
                      <td><code style={{ fontSize: 12 }}>{p.slug}</code></td>
                      <td>{p.title}</td>
                      <td>{p.sort_order}</td>
                      <td>{p.published ? 'Ja' : 'Nee'}</td>
                      <td>
                        <button type="button" className="btn btn-secondary btn-small" style={{ marginRight: 4 }} onClick={() => startEditPage(p)}>Bewerken</button>
                        <button type="button" className="btn btn-secondary btn-small" style={{ color: '#dc2626' }} onClick={() => deletePage(p.id)}>Verwijderen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pages.length === 0 && !loading && (
                <p style={{ padding: 16, color: '#6b7280', margin: 0 }}>Nog geen pagina&apos;s.</p>
              )}
            </div>
          )}

          {(editingPageId === 'new' || editingPageId) && (
            <form onSubmit={savePage} style={{ marginTop: 24, padding: 20, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <h4 style={{ marginTop: 0 }}>{editingPageId === 'new' ? 'Nieuwe pagina' : 'Pagina bewerken'}</h4>
              <div className="input-group">
                <label>Slug (URL: /pagina/{'<slug>'})</label>
                <input
                  type="text"
                  value={pageForm.slug}
                  onChange={e => setPageForm(f => ({ ...f, slug: e.target.value }))}
                  placeholder="over-ons"
                  required
                />
              </div>
              <div className="input-group">
                <label>Titel</label>
                <input
                  type="text"
                  value={pageForm.title}
                  onChange={e => setPageForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div className="input-group">
                <label>Meta titel (SEO, optioneel)</label>
                <input
                  type="text"
                  value={pageForm.meta_title}
                  onChange={e => setPageForm(f => ({ ...f, meta_title: e.target.value }))}
                />
              </div>
              <div className="input-group">
                <label>Meta description (SEO, max ~160 tekens)</label>
                <input
                  type="text"
                  value={pageForm.meta_description}
                  onChange={e => setPageForm(f => ({ ...f, meta_description: e.target.value }))}
                />
              </div>
              <div className="input-group">
                <label>Inhoud (HTML)</label>
                <textarea
                  value={pageForm.body}
                  onChange={e => setPageForm(f => ({ ...f, body: e.target.value }))}
                  rows={12}
                  style={{ fontFamily: 'inherit', fontSize: 13, width: '100%' }}
                />
              </div>
              <div className="input-group">
                <label>Sorteervolgorde (lager = eerder)</label>
                <input
                  type="number"
                  value={pageForm.sort_order}
                  onChange={e => setPageForm(f => ({ ...f, sort_order: e.target.value }))}
                  style={{ width: 80 }}
                />
              </div>
              <div className="input-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageForm.published}
                    onChange={e => setPageForm(f => ({ ...f, published: e.target.checked }))}
                  />
                  <span>Gepubliceerd</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Opslaan...' : 'Opslaan'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingPageId(null)}>Annuleren</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default CmsAdmin;
